import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import cable from '../cable';
import { logDebug, logError } from '../lib/logger';
import { publishWorkspacePresencePayload, removeWorkspacePresence } from '../lib/workspace_presence_store';

const HEARTBEAT_INTERVAL_MS = 20_000;
// Ticks faster than the heartbeat so a failed enter retries promptly; heartbeatCurrentWorkspace
// throttles the actual heartbeats to HEARTBEAT_INTERVAL_MS.
const PRESENCE_TICK_MS = 2_500;

function normalizeId(id) {
  if (id === null || id === undefined || id === '') return null;
  return String(id);
}

function getPresencePlatform() {
  if (window.electron) return 'desktop';

  const userAgent = navigator.userAgent || '';
  const isIos = /iPad|iPhone|iPod/.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (isIos) return 'ios_web';

  return 'web';
}

function generateClientInstanceId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

// Intentionally memory-only: this id models one loaded app instance, not a
// browser tab across reloads. SPA workspace switches keep this module alive, so
// the server can retire the old workspace immediately. Reloads/full restarts get
// a fresh id and rely on the old WebSocket's leave/TTL cleanup, which is fine.
// Do not persist this in sessionStorage: Duplicate Tab copies sessionStorage,
// which would make two independent tabs evict each other's workspace presence.
const clientInstanceId = generateClientInstanceId();

function inactivePresenceState() {
  return { workspaceId: null, sessionId: null, present: false, entering: false, lastHeartbeatAt: 0, viewingChannelId: null };
}

function computeDocumentVisible() {
  if (typeof document === 'undefined') return false;
  return document.visibilityState === 'visible';
}

function usePresenceDocumentVisible() {
  const [documentVisible, setDocumentVisible] = useState(() => computeDocumentVisible());

  useEffect(() => {
    const sync = () => setDocumentVisible(computeDocumentVisible());
    document.addEventListener('visibilitychange', sync);
    sync();
    return () => document.removeEventListener('visibilitychange', sync);
  }, []);

  return documentVisible;
}

export function useWorkspacePresence({ workspaceId: rawWorkspaceId, disabled = false, viewingChannelId: rawViewingChannelId = null }) {
  const documentVisible = usePresenceDocumentVisible();
  const workspaceId = useMemo(() => normalizeId(rawWorkspaceId), [rawWorkspaceId]);
  const viewingChannelId = useMemo(() => normalizeId(rawViewingChannelId), [rawViewingChannelId]);
  const platform = useMemo(() => getPresencePlatform(), []);
  const shouldBePresent = Boolean(workspaceId && documentVisible && !disabled);
  const desiredRef = useRef({ workspaceId: null, shouldBePresent: false, viewingChannelId: null });
  const currentRef = useRef(inactivePresenceState());
  const subscriptionRef = useRef(null);

  const perform = useCallback((action, payload = {}, { workspaceId: expectedWorkspaceId = null } = {}) => {
    const entry = subscriptionRef.current;
    if (!entry) return false;

    const normalizedExpectedWorkspaceId = normalizeId(expectedWorkspaceId);
    if (normalizedExpectedWorkspaceId && entry.workspaceId !== normalizedExpectedWorkspaceId) {
      logDebug('workspace-presence', 'perform skipped: workspace mismatch', {
        action,
        expectedWorkspaceId: normalizedExpectedWorkspaceId,
        subscriptionWorkspaceId: entry.workspaceId,
      });
      return false;
    }

    try {
      entry.subscription
        .perform(action, payload)
        .catch((error) => {
          logDebug('workspace-presence', 'perform rejected', { action, workspaceId: entry.workspaceId, error: error?.message || String(error) });
        });
      return true;
    } catch (error) {
      logError('workspace-presence', `Failed to perform ${action}`, error);
      return false;
    }
  }, []);

  const enterWorkspace = useCallback(() => {
    const desired = desiredRef.current;
    if (!desired.workspaceId || !desired.shouldBePresent) return;
    if (!subscriptionRef.current) return;
    if (currentRef.current.present && currentRef.current.workspaceId === desired.workspaceId) return;
    if (currentRef.current.entering && currentRef.current.workspaceId === desired.workspaceId) return;

    currentRef.current = {
      workspaceId: desired.workspaceId,
      sessionId: null,
      present: false,
      entering: true,
      lastHeartbeatAt: Date.now(),
      viewingChannelId: desired.viewingChannelId,
    };
    if (
      !perform(
        'enter',
        { platform, client_instance_id: clientInstanceId, viewing_channel_id: desired.viewingChannelId },
        { workspaceId: desired.workspaceId },
      )
    ) {
      currentRef.current = inactivePresenceState();
    }
  }, [perform, platform]);

  const leaveCurrentWorkspace = useCallback(() => {
    const current = currentRef.current;
    if (!current.present || !current.sessionId) {
      currentRef.current = inactivePresenceState();
      return;
    }

    perform('leave', { session_id: current.sessionId }, { workspaceId: current.workspaceId });
    currentRef.current = inactivePresenceState();
  }, [perform]);

  const heartbeatCurrentWorkspace = useCallback(
    ({ force = false } = {}) => {
      const current = currentRef.current;
      if (current.entering) return;

      if (!current.present || !current.sessionId) {
        enterWorkspace();
        return;
      }

      const now = Date.now();
      if (!force && now - current.lastHeartbeatAt < HEARTBEAT_INTERVAL_MS) return;

      currentRef.current = { ...current, lastHeartbeatAt: now };
      perform('heartbeat', { session_id: current.sessionId }, { workspaceId: current.workspaceId });
    },
    [enterWorkspace, perform],
  );

  // Route changes update server-side viewing state immediately rather than on the next heartbeat;
  // heartbeats no longer carry viewing_channel_id, so this is the only path that updates it.
  const syncViewingChannel = useCallback(() => {
    const current = currentRef.current;
    const desired = desiredRef.current;
    if (current.entering) return;
    if (!current.present || !current.sessionId) return;
    if (current.workspaceId !== desired.workspaceId) return;
    if (current.viewingChannelId === desired.viewingChannelId) return;

    if (perform('viewing', { session_id: current.sessionId, viewing_channel_id: desired.viewingChannelId }, { workspaceId: current.workspaceId })) {
      currentRef.current = { ...current, viewingChannelId: desired.viewingChannelId };
    }
  }, [perform]);

  const syncWorkspacePresence = useCallback(
    ({ force = false } = {}) => {
      const desired = desiredRef.current;

      if (!desired.shouldBePresent) {
        leaveCurrentWorkspace();
        return;
      }

      if (currentRef.current.present && currentRef.current.workspaceId !== desired.workspaceId) {
        leaveCurrentWorkspace();
      }

      heartbeatCurrentWorkspace({ force });
    },
    [heartbeatCurrentWorkspace, leaveCurrentWorkspace],
  );

  useEffect(() => {
    desiredRef.current = { workspaceId, shouldBePresent, viewingChannelId: shouldBePresent ? viewingChannelId : null };
  }, [shouldBePresent, viewingChannelId, workspaceId]);

  useEffect(() => {
    syncWorkspacePresence();
  }, [shouldBePresent, syncWorkspacePresence, workspaceId]);

  useEffect(() => {
    syncViewingChannel();
  }, [shouldBePresent, syncViewingChannel, viewingChannelId, workspaceId]);

  useEffect(() => {
    if (!shouldBePresent) return undefined;

    const tick = window.setInterval(syncWorkspacePresence, PRESENCE_TICK_MS);
    return () => window.clearInterval(tick);
  }, [shouldBePresent, syncWorkspacePresence]);

  useEffect(() => {
    const handlePageExit = () => leaveCurrentWorkspace();
    window.addEventListener('pagehide', handlePageExit);
    return () => window.removeEventListener('pagehide', handlePageExit);
  }, [leaveCurrentWorkspace]);

  useEffect(() => {
    if (!workspaceId || disabled) return undefined;

    const subscription = cable.subscribeTo('WorkspacePresenceChannel', { workspace_id: workspaceId });
    const subscriptionEntry = { workspaceId, subscription };
    subscriptionRef.current = subscriptionEntry;
    const isActiveSubscription = () => subscriptionRef.current === subscriptionEntry;

    const unbindMessage = subscription.on('message', (message) => {
      if (!isActiveSubscription()) return;

      publishWorkspacePresencePayload(message);

      switch (message?.action) {
        case 'presence_entered': {
          const enteredViewingChannelId = currentRef.current.viewingChannelId || null;
          currentRef.current = {
            workspaceId: normalizeId(message.data?.workspace_id) || workspaceId,
            sessionId: message.data?.session_id || null,
            present: !!message.data?.session_id,
            entering: false,
            lastHeartbeatAt: Date.now(),
            viewingChannelId: enteredViewingChannelId,
          };
          logDebug('workspace-presence', 'entered workspace', { workspaceId });
          if (!desiredRef.current.shouldBePresent || desiredRef.current.workspaceId !== currentRef.current.workspaceId) {
            leaveCurrentWorkspace();
          } else {
            syncViewingChannel();
          }
          break;
        }
        case 'presence_session_missing':
          currentRef.current = inactivePresenceState();
          logDebug('workspace-presence', 'session missing; re-entering', { workspaceId });
          enterWorkspace();
          break;
      }
    });

    const unbindConnect = subscription.on('connect', () => {
      if (!isActiveSubscription()) return;

      perform('sync', {}, { workspaceId });
      // force past the throttle: the server may have expired this session while the socket was down
      syncWorkspacePresence({ force: true });
    });

    const unbindDisconnect = subscription.on('disconnect', (error) => {
      if (!isActiveSubscription()) return;

      logDebug('workspace-presence', 'subscription disconnected', { workspaceId, error: error?.message || String(error || '') });
    });

    syncWorkspacePresence();

    return () => {
      leaveCurrentWorkspace();
      unbindMessage();
      unbindConnect();
      unbindDisconnect();
      subscription.disconnect();
      if (subscriptionRef.current === subscriptionEntry) subscriptionRef.current = null;
      currentRef.current = inactivePresenceState();
      removeWorkspacePresence(workspaceId);
    };
  }, [disabled, enterWorkspace, leaveCurrentWorkspace, perform, syncViewingChannel, syncWorkspacePresence, workspaceId]);
}

export default useWorkspacePresence;
